// What the AI Worker (apps/ai) and the web app exchange, and the tools the model answers through.
// Each feature has one tool with a strict JSON schema; the Worker checks the model's answer with the read*
// functions below (types, lengths, enums) and clamps numbers itself, because strict schemas do not support
// minimum or maximum. The web app reads the Worker's answer with the same functions, so a bad answer can never
// reach a form. Numbers the model did not find are 0 ("not given"), never null, which keeps the schemas simple.
import { MUSCLES } from '../catalog/define';
import { IMPORT_LIMITS } from '../import/parseText';
import { CARDIO_SPORT_IDS, CARDIO_SPORTS, isCardioSport } from '../cardio/sports';
import { EQUIPMENT_LABELS, type CardioSport, type CameraView, type CountingMode, type Equipment, type ExerciseCategory, type Level, type SectionKind } from '../types';

// Limits -------------------------------------------------------------------------------------------

/** Page text sent to the model, in characters (about 6'000 tokens). */
export const AI_PAGE_MAX_CHARS = 24_000;
/** A photo after resizing on the phone: base64 text of the JPEG. About 3 MB of image data. */
export const AI_PHOTO_MAX_BASE64 = 4_000_000;
/** The longest side the phone resizes a photo to. */
export const AI_PHOTO_MAX_SIDE = 1600;
export const AI_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AiPhotoType = (typeof AI_PHOTO_TYPES)[number];
/** What a picture really is, from its first bytes (never trust a file name or a declared type). */
export type SniffedImage = AiPhotoType | 'image/heic' | 'image/gif' | null;

export function sniffImage(b: Uint8Array): SniffedImage {
  const at = (i: number, ...v: number[]) => v.every((x, k) => b[i + k] === x);
  const ascii = (i: number, text: string) => [...text].every((ch, k) => b[i + k] === ch.charCodeAt(0));
  if (at(0, 0xff, 0xd8, 0xff)) return 'image/jpeg';
  if (at(0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'image/png';
  if (ascii(0, 'RIFF') && ascii(8, 'WEBP')) return 'image/webp';
  if (ascii(0, 'GIF8')) return 'image/gif';
  if (ascii(4, 'ftyp') && ['heic', 'heix', 'hevc', 'heim', 'heis', 'mif1', 'msf1', 'avif'].some((brand) => ascii(8, brand))) return 'image/heic';
  return null;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** The first bytes of base64 text (enough to sniff the type), without decoding the whole picture. */
export function base64Head(text: string, bytes = 16): Uint8Array {
  const chars = text.slice(0, Math.ceil(bytes / 3) * 4);
  const out: number[] = [];
  for (let i = 0; i < chars.length; i += 4) {
    const n = [0, 1, 2, 3].map((k) => B64.indexOf(chars[i + k] ?? 'A'));
    if (n.some((x) => x < 0)) break;
    const v = (n[0]! << 18) | (n[1]! << 12) | (n[2]! << 6) | n[3]!;
    out.push((v >> 16) & 255, (v >> 8) & 255, v & 255);
  }
  return new Uint8Array(out.slice(0, bytes));
}

/** Names per name matching call, and library entries sent with them. */
export const AI_MATCH_MAX_NAMES = 30;
export const AI_MATCH_MAX_LIBRARY = 1500;

// Small readers -------------------------------------------------------------------------------------

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
/** Trimmed single-line text, cut to max characters. */
export function cleanText(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is removed here
  return v.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max).trim();
}
const int = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0);
const oneOf = <T extends string>(v: unknown, values: readonly T[], fallback: T): T => (values.includes(v as T) ? (v as T) : fallback);
const clamp = (n: number, [min, max]: readonly [number, number]) => Math.min(max, Math.max(min, n));
/** 0 stays 0 (not given); anything else is kept within the builder's limits. */
const clampGiven = (n: number, range: readonly [number, number]) => (n <= 0 ? 0 : clamp(n, range));
const list = (v: unknown, max: number): unknown[] => (Array.isArray(v) ? v.slice(0, max) : []);

// Workout drafts (import from a link or a photo) ---------------------------------------------------

export const AI_FORMATS = ['list', 'circuit', 'sets', 'emom', 'amrap', 'continuous'] as const;
export type AiFormat = (typeof AI_FORMATS)[number];
const KINDS: readonly SectionKind[] = ['warmup', 'main', 'cooldown'];

export interface AiWorkoutExercise {
  name: string;
  amountKind: 'reps' | 'time' | 'none';
  reps: number;
  repsMax: number;
  seconds: number;
  perSide: boolean;
  sets: number;
  note: string;
  /** The model guessed the name or the amount. */
  unsure: boolean;
}

export interface AiWorkoutSection {
  kind: SectionKind;
  format: AiFormat;
  rounds: number;
  workSec: number;
  restSec: number;
  roundRestSec: number;
  sets: number;
  minutes: number;
  exercises: AiWorkoutExercise[];
}

export interface AiWorkout {
  /** False when the page or photo holds no workout. */
  found: boolean;
  title: string;
  sections: AiWorkoutSection[];
  /** The model's notes on what it guessed, in plain words. */
  unsure: string[];
}

const n = (description: string) => ({ type: 'integer', description });

export const WORKOUT_TOOL = {
  name: 'record_workout',
  description: 'Record the workout found in the page text or photo, in the structure the fitness app needs.',
  strict: true,
  input_schema: {
    type: 'object',
    additionalProperties: false,
    required: ['found', 'title', 'sections', 'unsure'],
    properties: {
      found: { type: 'boolean', description: 'false when there is no workout at all (then sections is empty).' },
      title: { type: 'string', description: 'A short workout name in English, at most 60 characters. Empty when none fits.' },
      sections: {
        type: 'array',
        description: 'Warm-up, main parts and cool-down in order. Each sets-and-reps scheme or each timed block is its own main part.',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['kind', 'format', 'rounds', 'workSec', 'restSec', 'roundRestSec', 'sets', 'minutes', 'exercises'],
          properties: {
            kind: { type: 'string', enum: [...KINDS] },
            format: {
              type: 'string', enum: [...AI_FORMATS],
              description: 'list: one after the other; circuit: rounds of the list with work and rest seconds; sets: sets of each exercise with rest; emom: every minute on the minute; amrap: as many rounds as possible; continuous: non-stop for minutes.',
            },
            rounds: n('circuit rounds, 0 when not given'),
            workSec: n('circuit work seconds per exercise, 0 when each exercise has its own amount or not given'),
            restSec: n('rest seconds between exercises (circuit) or between sets (sets), 0 when none or not given'),
            roundRestSec: n('circuit rest seconds between rounds, 0 when none or not given'),
            sets: n('sets per exercise for format sets, 0 otherwise'),
            minutes: n('total minutes for emom, amrap and continuous, 0 otherwise'),
            exercises: {
              type: 'array',
              items: {
                type: 'object',
                additionalProperties: false,
                required: ['name', 'amountKind', 'reps', 'repsMax', 'seconds', 'perSide', 'sets', 'note', 'unsure'],
                properties: {
                  name: { type: 'string', description: 'The exercise name in English, short and plain (for example "Kettlebell swing"), without amounts.' },
                  amountKind: { type: 'string', enum: ['reps', 'time', 'none'] },
                  reps: n('reps, or the low end of a rep range; 0 when not reps'),
                  repsMax: n('the high end of a rep range, else 0'),
                  seconds: n('seconds for a timed exercise, else 0'),
                  perSide: { type: 'boolean', description: 'true when the amount is per side or per arm or leg' },
                  sets: n('sets for this exercise when stated, else 0'),
                  note: { type: 'string', description: 'A short fact in your own words: weight ("16 kg"), distance ("200 m") or tempo. Empty when none.' },
                  unsure: { type: 'boolean', description: 'true when the name or the amount is a guess' },
                },
              },
            },
          },
        },
      },
      unsure: { type: 'array', items: { type: 'string' }, description: 'Short notes on what you had to guess, in plain English. Empty when nothing.' },
    },
  },
} as const;

const L = IMPORT_LIMITS;

/** Checks and clamps a model's (or the Worker's) workout answer. Null when it is not a workout object at all. */
export function readAiWorkout(input: unknown): AiWorkout | null {
  if (!isObj(input)) return null;
  const sections: AiWorkoutSection[] = [];
  for (const raw of list(input.sections, 12)) {
    if (!isObj(raw)) continue;
    const exercises: AiWorkoutExercise[] = [];
    for (const e of list(raw.exercises, 40)) {
      if (!isObj(e)) continue;
      const name = cleanText(e.name, 80);
      if (!name) continue;
      const amountKind = oneOf(e.amountKind, ['reps', 'time', 'none'] as const, 'none');
      const reps = clampGiven(int(e.reps), L.reps);
      const repsMax = clampGiven(int(e.repsMax), L.reps);
      exercises.push({
        name, amountKind, reps,
        repsMax: repsMax > reps ? repsMax : 0,
        seconds: clampGiven(int(e.seconds), L.seconds),
        perSide: e.perSide === true,
        sets: clampGiven(int(e.sets), L.sets),
        note: cleanText(e.note, 60),
        unsure: e.unsure === true,
      });
    }
    if (!exercises.length) continue;
    sections.push({
      kind: oneOf(raw.kind, KINDS, 'main'),
      format: oneOf(raw.format, AI_FORMATS, 'list'),
      rounds: clampGiven(int(raw.rounds), L.rounds),
      workSec: clampGiven(int(raw.workSec), L.workSec),
      restSec: clamp(Math.max(0, int(raw.restSec)), L.restSec),
      roundRestSec: clamp(Math.max(0, int(raw.roundRestSec)), L.roundRestSec),
      sets: clampGiven(int(raw.sets), L.sets),
      minutes: clampGiven(int(raw.minutes), L.minutes),
      exercises,
    });
  }
  return {
    found: input.found !== false && sections.length > 0,
    title: cleanText(input.title, 80),
    sections,
    unsure: list(input.unsure, 8).map((u) => cleanText(u, 160)).filter(Boolean),
  };
}

// Activity screenshots (a Strava stats picture or a screenshot of an activity) -------------------------

export const AI_ACTIVITY_FIELDS = ['sport', 'date', 'startTime', 'movingSec', 'elapsedSec', 'distanceM', 'elevationGainM', 'avgHr', 'maxHr', 'title'] as const;
export type AiActivityField = (typeof AI_ACTIVITY_FIELDS)[number];

export interface AiActivity {
  /** False when the picture shows no activity. */
  found: boolean;
  /** One of our sports, or null when the picture does not tell. */
  sport: CardioSport | null;
  /** A variant of that sport ('' when unknown). */
  variant: string;
  /** "YYYY-MM-DD" or '' when not shown. */
  date: string;
  /** "HH:MM" or '' when not shown. */
  startTime: string;
  /** 0 means not shown. */
  movingSec: number;
  elapsedSec: number;
  distanceM: number;
  elevationGainM: number;
  avgHr: number;
  maxHr: number;
  /** The activity's name as shown ("Morning Run"), '' when none. */
  title: string;
  unsure: AiActivityField[];
}

const ALL_VARIANTS = [...new Set(CARDIO_SPORT_IDS.flatMap((s) => Object.keys(CARDIO_SPORTS[s].variants)))];

export const ACTIVITY_TOOL = {
  name: 'record_activity',
  description: 'Record the numbers of the recorded activity (run, ride, swim, hike) shown in the picture.',
  strict: true,
  input_schema: {
    type: 'object',
    additionalProperties: false,
    required: ['found', 'sport', 'variant', 'date', 'startTime', 'movingSec', 'elapsedSec', 'distanceM', 'elevationGainM', 'avgHr', 'maxHr', 'title', 'unsure'],
    properties: {
      found: { type: 'boolean', description: 'false when the picture shows no recorded activity.' },
      sport: { type: 'string', enum: [...CARDIO_SPORT_IDS, 'unknown'], description: 'The sport (from the sport word or icon); unknown when it cannot be told.' },
      variant: { type: 'string', enum: [...ALL_VARIANTS, ''], description: 'trail, treadmill, gravel, indoor, pool and so on when shown, else empty.' },
      date: { type: 'string', description: 'The day as YYYY-MM-DD when the picture shows it, else empty. Never guess the year from nothing: empty then.' },
      startTime: { type: 'string', description: 'The start time as HH:MM (24 hours) when shown, else empty.' },
      movingSec: n('moving time in seconds ("Moving Time", "Time"); 0 when not shown'),
      elapsedSec: n('elapsed time in seconds when shown separately, else 0'),
      distanceM: n('distance in meters (10.52 km is 10520, 6.2 mi is 9978); 0 when not shown'),
      elevationGainM: n('elevation gain in meters (feet times 0.3048); 0 when not shown'),
      avgHr: n('average heart rate in bpm, 0 when not shown'),
      maxHr: n('maximum heart rate in bpm, 0 when not shown'),
      title: { type: 'string', description: 'The activity name as shown, at most 80 characters; empty when none.' },
      unsure: { type: 'array', items: { type: 'string', enum: [...AI_ACTIVITY_FIELDS] }, description: 'Fields you guessed or computed (for example the time from pace and distance).' },
    },
  },
} as const;

/** Checks and clamps a model's (or the Worker's) activity answer. Null when it is not an object at all. */
export function readAiActivity(input: unknown): AiActivity | null {
  if (!isObj(input)) return null;
  const sport = isCardioSport(input.sport) ? input.sport : null;
  const variant = sport && typeof input.variant === 'string' && Object.hasOwn(CARDIO_SPORTS[sport].variants, input.variant) ? input.variant : '';
  // A real calendar day only: Date.parse turns 2026-02-30 into March 2, so the day must survive the round trip.
  const realDay = (s: string) => { const t = Date.parse(`${s}T00:00:00Z`); return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === s; };
  const date = typeof input.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input.date) && realDay(input.date) ? input.date : '';
  const time = typeof input.startTime === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(input.startTime) ? input.startTime : '';
  const within = (v: unknown, max: number) => { const x = int(v); return x > 0 && x <= max ? x : 0; };
  const hr = (v: unknown) => { const x = int(v); return x >= 30 && x <= 250 ? x : 0; };
  const avgHr = hr(input.avgHr);
  const maxHr = hr(input.maxHr);
  const movingSec = within(input.movingSec, 48 * 3600);
  const out: AiActivity = {
    found: false,
    sport, variant, date, startTime: time,
    movingSec,
    elapsedSec: within(input.elapsedSec, 96 * 3600),
    distanceM: within(input.distanceM, 1_000_000),
    elevationGainM: within(input.elevationGainM, 20_000),
    avgHr,
    maxHr: maxHr && avgHr && maxHr < avgHr ? 0 : maxHr,
    title: cleanText(input.title, 80),
    unsure: [...new Set(list(input.unsure, 12).filter((f): f is AiActivityField => (AI_ACTIVITY_FIELDS as readonly string[]).includes(f as string)))],
  };
  // Without any time or distance there is nothing to log.
  out.found = input.found !== false && (out.movingSec > 0 || out.distanceM > 0);
  return out;
}

// Name matching help --------------------------------------------------------------------------------

export interface AiMatchAnswer {
  name: string;
  /** A library exercise id, or null when none fits. */
  exerciseId: string | null;
}

export const MATCH_TOOL = {
  name: 'record_matches',
  description: 'Record, for each name, the library exercise it means, or an empty id when none fits.',
  strict: true,
  input_schema: {
    type: 'object',
    additionalProperties: false,
    required: ['matches'],
    properties: {
      matches: {
        type: 'array',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['name', 'exerciseId'],
          properties: {
            name: { type: 'string', description: 'The name exactly as given.' },
            exerciseId: { type: 'string', description: 'The id from the library list, or "" when no exercise in the list is the same movement.' },
          },
        },
      },
    },
  },
} as const;

/** Keeps answers for the asked names whose id is in the library sent; anything else becomes "no match". */
export function readAiMatches(input: unknown, names: string[], libraryIds: Set<string>): AiMatchAnswer[] {
  const given = new Map<string, string>();
  if (isObj(input)) {
    for (const m of list(input.matches, AI_MATCH_MAX_NAMES * 2)) {
      if (!isObj(m) || typeof m.name !== 'string' || typeof m.exerciseId !== 'string') continue;
      if (libraryIds.has(m.exerciseId)) given.set(m.name.trim().toLowerCase(), m.exerciseId);
    }
  }
  return names.map((name) => ({ name, exerciseId: given.get(name.trim().toLowerCase()) ?? null }));
}

// New exercise drafts --------------------------------------------------------------------------------

export const AI_EXERCISE_FIELDS = ['aliases', 'equipment', 'muscles', 'steps', 'countingMode', 'cameraView', 'level', 'intensity', 'category', 'unilateral'] as const;
export type AiExerciseField = (typeof AI_EXERCISE_FIELDS)[number];

export interface AiExerciseDraft {
  aliases: string[];
  equipment: Equipment;
  extraEquipment: Equipment[];
  muscles: string[];
  steps: string[];
  countingMode: CountingMode;
  cameraView: CameraView;
  level: Level;
  intensity: 1 | 2 | 3;
  category: ExerciseCategory | '';
  unilateral: boolean;
  /** Fields the model was not sure about; the form marks them. */
  unsure: AiExerciseField[];
}

const EQUIPMENT = Object.keys(EQUIPMENT_LABELS) as Equipment[];
const CATEGORIES: readonly ExerciseCategory[] = ['strength', 'power', 'plyometric', 'cardio', 'core', 'mobility', 'stretch', 'breathing'];
const VIEWS: readonly CameraView[] = ['front', 'diagonal', 'side', 'any'];
const MODES: readonly CountingMode[] = ['camera', 'time', 'manual'];
const LEVELS: readonly Level[] = ['beginner', 'intermediate', 'advanced'];
const EFFORT = ['light', 'moderate', 'hard'] as const;

export const EXERCISE_TOOL = {
  name: 'record_exercise',
  description: 'Record a draft of the exercise for the fitness app library.',
  strict: true,
  input_schema: {
    type: 'object',
    additionalProperties: false,
    required: ['aliases', 'equipment', 'extraEquipment', 'muscles', 'steps', 'countingMode', 'cameraView', 'level', 'effort', 'category', 'unilateral', 'unsure'],
    properties: {
      aliases: { type: 'array', items: { type: 'string' }, description: 'Up to 4 other common names (English, and German when common).' },
      equipment: { type: 'string', enum: EQUIPMENT, description: 'The main equipment; bodyweight when none.' },
      extraEquipment: { type: 'array', items: { type: 'string', enum: EQUIPMENT }, description: 'Further equipment it needs (for example bench), else empty.' },
      muscles: { type: 'array', items: { type: 'string', enum: [...MUSCLES] }, description: '1 to 5 muscles it mainly works.' },
      steps: { type: 'array', items: { type: 'string' }, description: '2 to 4 short steps in your own words: how to do it and what to watch.' },
      countingMode: { type: 'string', enum: [...MODES], description: 'camera: repeated visible movement a camera can count (squat, swing); time: holds and stretches; manual: everything else.' },
      cameraView: { type: 'string', enum: [...VIEWS], description: 'Where a camera sees the movement best.' },
      level: { type: 'string', enum: [...LEVELS] },
      effort: { type: 'string', enum: [...EFFORT] },
      category: { type: 'string', enum: [...CATEGORIES, 'none'] },
      unilateral: { type: 'boolean', description: 'true when done one side at a time' },
      unsure: { type: 'array', items: { type: 'string', enum: [...AI_EXERCISE_FIELDS] }, description: 'Fields you are not sure about.' },
    },
  },
} as const;

/** Checks a model's (or the Worker's) exercise answer. Null when it is unusable (no muscle or no step). */
export function readAiExercise(input: unknown): AiExerciseDraft | null {
  if (!isObj(input)) return null;
  const muscles = [...new Set(list(input.muscles, 8).filter((m): m is string => typeof m === 'string' && (MUSCLES as readonly string[]).includes(m)))].slice(0, 5);
  const steps = list(input.steps, 6).map((s) => cleanText(s, 200)).filter(Boolean).slice(0, 4);
  if (!muscles.length || !steps.length) return null;
  const equipment = oneOf(input.equipment, EQUIPMENT, 'bodyweight');
  // The model answers with an effort word; the Worker's own answer carries the intensity number.
  const intensity = [1, 2, 3].includes(input.intensity as number) ? (input.intensity as 1 | 2 | 3) : ((EFFORT.indexOf(oneOf(input.effort, EFFORT, 'moderate')) + 1) as 1 | 2 | 3);
  const category = oneOf(input.category, [...CATEGORIES, 'none', ''] as const, 'none');
  return {
    aliases: [...new Set(list(input.aliases, 8).map((a) => cleanText(a, 60)).filter(Boolean))].slice(0, 4),
    equipment,
    extraEquipment: [...new Set(list(input.extraEquipment, 4).filter((x): x is Equipment => EQUIPMENT.includes(x as Equipment) && x !== equipment))],
    muscles,
    steps,
    countingMode: oneOf(input.countingMode, MODES, 'manual'),
    cameraView: oneOf(input.cameraView, VIEWS, 'front'),
    level: oneOf(input.level, LEVELS, 'beginner'),
    intensity,
    category: category === 'none' ? '' : category,
    unilateral: input.unilateral === true,
    unsure: [...new Set(list(input.unsure, 12).filter((f): f is AiExerciseField => (AI_EXERCISE_FIELDS as readonly string[]).includes(f as string)))],
  };
}

// Web app <-> Worker -----------------------------------------------------------------------------------

export interface AiLinkRequest { url: string }
/** kind 'activity': a Strava stats picture or an activity screenshot, read into an activity draft (ACTIVITY_TOOL). */
export interface AiPhotoRequest { image: string; mediaType: AiPhotoType; kind?: 'workout' | 'activity' }
export interface AiMatchRequest { names: string[]; library: { id: string; name: string }[] }
export interface AiExerciseRequest { name: string }

/** The Worker's answer to an accepted call. */
export interface AiReply<T> {
  result: T;
  model: string;
  /** What this call cost, in USD. */
  costUsd: number;
}

/** GET-like status call: whether AI is set up and on for this person. */
export interface AiStatus {
  ready: true;
  features: string[];
}
